import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { api } from '@/api/client'
import type { AppLocale } from '@/config/locale'
import { getLocale } from '@/config/locale'

const nullableString = z.string().nullable().optional()
const nullableNumber = z.number().nullable().optional()

const teamSchema = z
  .object({
    id: nullableNumber,
    name: nullableString,
    display_name: nullableString,
    short_name: nullableString,
    logo_url: nullableString,
    is_placeholder: z.boolean().optional(),
  })
  .passthrough()

const predictionSchema = z
  .object({
    id: nullableNumber,
    home_pred: nullableNumber,
    away_pred: nullableNumber,
    points_awarded: nullableNumber,
    virtual_points: nullableNumber,
    poule_id: nullableNumber,
  })
  .passthrough()

const matchSchema = z
  .object({
    id: z.number(),
    kickoff_utc: nullableString,
    kickoff_nl: nullableString,
    status: nullableString,
    joker: z.boolean().optional(),
    joker_multiplier: z.number().optional(),
    is_predictable: z.boolean().optional(),
    minute: z.number().nullable().optional(),
    extra_minute: z.number().nullable().optional(),
    round: z.union([z.number(), z.string()]).nullable().optional(),
    is_cancelled: z.boolean().optional(),
    predict_status: nullableString,
    poule_id: nullableNumber,
    poule_match_id: nullableNumber,
    score: z
      .object({
        home: z.number().nullable().optional(),
        away: z.number().nullable().optional(),
      })
      .passthrough()
      .optional(),
    home_team: teamSchema.optional(),
    away_team: teamSchema.optional(),
    user_prediction: predictionSchema.nullable().optional(),
    chat_active: z.boolean().optional(),
    chat_writable: z.boolean().optional(),
  })
  .passthrough()

const pouleSchema = z
  .object({
    id: z.number(),
    name: z.string(),
    poule_type: nullableString,
    scope_type: nullableString,
    thumbnail_url: nullableString,
    prediction_deadline_context: z.record(z.string(), z.unknown()).optional(),
    matches: z.array(matchSchema).optional().default([]),
  })
  .passthrough()

const activitySchema = z
  .object({
    id: z.number(),
    type: z.string().optional(),
    published_at: z.string().optional(),
    text: z.string().optional(),
    fallback_text: z.string().optional(),
    actor: z
      .object({
        display_name: nullableString,
        profile_image: nullableString,
      })
      .passthrough()
      .optional(),
    poule: z
      .object({
        id: z.number().optional(),
        name: nullableString,
      })
      .passthrough()
      .optional(),
  })
  .passthrough()

const badgeTierSchema = z
  .object({
    key: nullableString,
    label: nullableString,
  })
  .passthrough()

const badgeRoundSchema = z
  .object({
    app_round_id: z.number().optional(),
    name: nullableString,
    sequence: z.number().optional(),
  })
  .passthrough()

const badgePouleSchema = z
  .object({
    id: z.number().optional(),
    name: nullableString,
    thumbnail_url: nullableString,
    competition: z
      .object({
        logo_url: nullableString,
      })
      .passthrough()
      .nullable()
      .optional(),
  })
  .passthrough()

const compactBadgeSchema = z
  .object({
    id: z.number().optional(),
    badge_id: z.number().optional(),
    badge_key: nullableString,
    name: nullableString,
    icon: nullableString,
    icon_url: nullableString,
    description: nullableString,
    category: nullableString,
    points_reward: z.number().optional(),
    difficulty: z.number().optional(),
    meta: z.record(z.string(), z.unknown()).nullable().optional(),
    payloadTierKey: nullableString,
    payloadTierLabel: nullableString,
    tier: badgeTierSchema.nullable().optional(),
    club: teamSchema.nullable().optional(),
    team: teamSchema.nullable().optional(),
    earned_at: nullableString,
    reason: nullableString,
    poule_id: z.number().nullable().optional(),
    poule: badgePouleSchema.nullable().optional(),
    prediction: predictionSchema.nullable().optional(),
    match: matchSchema.nullable().optional(),
    matches: z.array(matchSchema).optional().default([]),
    round: badgeRoundSchema.nullable().optional(),
    badge: z
      .object({
        id: z.number().optional(),
        key: nullableString,
        name: nullableString,
        icon_url: nullableString,
        description: nullableString,
        category: nullableString,
        points_reward: z.number().optional(),
        difficulty: z.number().optional(),
      })
      .passthrough()
      .optional(),
  })
  .passthrough()

const dashboardBadgesEarnedTodaySchema = z
  .object({
    badges_earned_today: z.array(compactBadgeSchema).optional().default([]),
  })
  .passthrough()

const joinedAnySchema = z
  .object({
    has_poules: z.boolean().optional(),
  })
  .passthrough()

const liveMatchesSummarySchema = z
  .object({
    matches_count: z.number().optional(),
    has_live_matches: z.boolean().optional(),
  })
  .passthrough()

const nextMatchesSummarySchema = z
  .object({
    date: nullableString,
    matches_count: z.number().optional(),
    unpredicted_count: z.number().optional(),
    has_matches: z.boolean().optional(),
    next_kickoff_local: nullableString,
    next_kickoff_utc: nullableString,
    match_center: z
      .object({
        mode: nullableString,
        availability_state: nullableString,
        regular: z
          .object({
            active: z.boolean().optional(),
            next_matches_total_count: z.number().optional(),
            next_matches_unpredicted_count: z.number().optional(),
            next_matches_open_unpredicted_count: z.number().optional(),
            next_matches_locked_unpredicted_count: z.number().optional(),
            availability_state: nullableString,
            is_ready_for_next_matches: z.boolean().optional(),
          })
          .passthrough()
          .nullable()
          .optional(),
        stage_by_stage: z
          .object({
            active: z.boolean().optional(),
            current_stage_total_matches_count: z.number().optional(),
            current_stage_unpredicted_matches_count: z.number().optional(),
            current_stage_open_unpredicted_matches_count: z.number().optional(),
            current_stage_locked_unpredicted_matches_count: z.number().optional(),
            availability_state: nullableString,
          })
          .passthrough()
          .nullable()
          .optional(),
      })
      .passthrough()
      .optional(),
  })
  .passthrough()

const dashboardSchema = z
  .object({
    user_id: z.number().optional(),
    date: z.string().optional(),
    points_date: nullableString,
    total_points: z.number().optional(),
    matches_count: z.number().optional(),
    badge_count: z.number().optional(),
    badges_earned_today_count: z.number().optional(),
    badges_earned_today: z.array(compactBadgeSchema).optional().default([]),
    poules: z.array(pouleSchema).optional().default([]),
    prediction_counts: z
      .object({
        perfect: z.number().optional(),
        wrong: z.number().optional(),
      })
      .passthrough()
      .optional(),
  })
  .passthrough()

const homeActivitiesSchema = z
  .object({
    items: z.array(activitySchema).optional().default([]),
    meta: z
      .object({
        has_more: z.boolean().optional(),
      })
      .passthrough()
      .optional(),
  })
  .passthrough()

const bootstrapSchema = z
  .object({
    joined_any: joinedAnySchema.optional(),
    live_matches_summary: liveMatchesSummarySchema.optional(),
    next_matches_summary: nextMatchesSummarySchema.optional(),
    dashboard: dashboardSchema.optional(),
    home_activities: homeActivitiesSchema.optional(),
  })
  .passthrough()

const recentMatchesSchema = z
  .object({
    user_id: nullableNumber,
    date: nullableString,
    total_points: z.number().optional(),
    poules: z.array(pouleSchema).optional().default([]),
  })
  .passthrough()

export type HomeBootstrap = z.infer<typeof bootstrapSchema>
export type HomeRecentMatches = z.infer<typeof recentMatchesSchema>
export type UserBadge = z.infer<typeof compactBadgeSchema>
export type DashboardBadgesEarnedTodayResponse = z.infer<
  typeof dashboardBadgesEarnedTodaySchema
>

async function fetchHomeBootstrap(): Promise<HomeBootstrap> {
  // Match Flutter's AppBootstrapApi: the shared bootstrap endpoint is kept
  // small and localized through the common API client headers/query.
  const response = await api.get<unknown>('/app/bootstrap')
  return parseBootstrapResponse(response)
}

async function fetchPouleHomeSummary(): Promise<HomeBootstrap> {
  // The poule-overarching dashboard only needs the compact football payload.
  // Keep this separate from the full bootstrap used by the success overview.
  const response = await api.get<unknown>(
    '/app/game-modes?context=home&platform=web',
  )

  if (!response || typeof response !== 'object') {
    throw new Error('Voetbalpoule-dashboard kon niet worden geladen.')
  }

  const games = (response as { games?: unknown }).games
  if (!Array.isArray(games)) {
    throw new Error('Voetbalpoule-dashboard kon niet worden geladen.')
  }

  const footballGame = games.find(
    (game): game is { key?: unknown; bootstrap?: unknown } =>
      Boolean(game) &&
      typeof game === 'object' &&
      (game as { key?: unknown }).key === 'football_poule',
  )

  if (!footballGame || !footballGame.bootstrap) {
    throw new Error('Voetbalpoule-dashboard kon niet worden geladen.')
  }

  return parseBootstrapResponse(footballGame.bootstrap)
}

export function useHomeBootstrap(
  locale: AppLocale = getLocale(),
  enabled = true,
) {
  return useQuery({
    queryKey: ['home-bootstrap', locale],
    queryFn: fetchHomeBootstrap,
    enabled,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  })
}

export function usePouleHomeSummary(
  locale: AppLocale = getLocale(),
  enabled = true,
) {
  return useQuery({
    queryKey: ['poule-home-summary', locale],
    queryFn: fetchPouleHomeSummary,
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  })
}

async function fetchRecentMatches(locale: AppLocale): Promise<HomeRecentMatches> {
  const response = await api.get<unknown>(
    '/users/me/recent-matches?only_with_matches=1',
  )
  const parsed = recentMatchesSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[home] Invalid recent-matches payload', parsed.error.flatten())
    throw new Error('Recente wedstrijden konden niet worden geladen.')
  }

  return parsed.data
}

export function useRecentMatches(locale: AppLocale = getLocale(), enabled = true) {
  return useQuery({
    queryKey: ['recent-matches', locale],
    queryFn: () => fetchRecentMatches(locale),
    enabled,
    staleTime: 30_000,
  })
}

export async function fetchDashboardBadgesEarnedToday(): Promise<UserBadge[]> {
  const response = await api.get<unknown>('/users/me/dashboard/badges-earned-today')
  const parsed = dashboardBadgesEarnedTodaySchema.safeParse(response)

  if (!parsed.success) {
    console.error('[home] Invalid badges-earned-today payload', parsed.error.flatten())
    throw new Error('Badges konden niet worden geladen.')
  }

  return parsed.data.badges_earned_today
}

function parseBootstrapResponse(response: unknown): HomeBootstrap {
  if (!response || typeof response !== 'object') {
    console.warn('[home] Bootstrap response is not an object')
    return {}
  }

  const source = response as Record<string, unknown>

  return {
    joined_any: parseSection('joined_any', joinedAnySchema, source.joined_any),
    live_matches_summary: parseSection(
      'live_matches_summary',
      liveMatchesSummarySchema,
      source.live_matches_summary,
    ),
    next_matches_summary: parseSection(
      'next_matches_summary',
      nextMatchesSummarySchema,
      source.next_matches_summary,
    ),
    dashboard: parseSection('dashboard', dashboardSchema, source.dashboard),
    home_activities: parseSection(
      'home_activities',
      homeActivitiesSchema,
      source.home_activities,
    ),
  }
}

function parseSection<S extends z.ZodTypeAny>(
  key: string,
  schema: S,
  value: unknown,
): z.output<S> | undefined {
  if (value === undefined) return undefined

  const parsed = schema.safeParse(value)
  if (parsed.success) return parsed.data

  console.warn(`[home] Ignoring invalid ${key} payload`, parsed.error.flatten())
  return undefined
}
